import React from 'react';
import { apTheme } from '../../utils/agentPlatformTheme';

type Tab<T extends string> = {
  id: T;
  label: string;
  icon?: React.ReactNode;
};

type Props<T extends string> = {
  tabs: Tab<T>[];
  active: T;
  onChange: (id: T) => void;
  variant?: 'default' | 'studio' | 'composer';
};

export function AgentPlatformTabs<T extends string>({
  tabs,
  active,
  onChange,
  variant = 'default',
}: Props<T>) {
  const activeClass =
    variant === 'composer'
      ? 'bg-[#c0392b] text-white shadow'
      : apTheme.tabActive;

  const idleClass =
    variant === 'composer'
      ? 'text-slate-400 hover:bg-white/10 hover:text-white'
      : apTheme.tabIdle;

  const barClass =
    variant === 'composer'
      ? 'flex gap-1 rounded-lg bg-white/5 p-1'
      : variant === 'studio'
        ? 'flex flex-wrap gap-1 rounded-xl bg-stone-100/80 p-1.5 ring-1 ring-stone-200/60'
        : 'flex flex-wrap gap-1 rounded-xl bg-slate-100/80 p-1.5 ring-1 ring-slate-200/60';

  return (
    <nav className={barClass} role="tablist">
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
            active === tab.id ? activeClass : idleClass
          }`}
        >
          {tab.icon}
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
